import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
// Portals to document.body, which is what makes it usable on this screen at
// all: the canvas sits under `transform: scale()` for the zoom, and a tooltip
// rendered inside that subtree would be scaled with it — shrinking the help
// text exactly when the user has zoomed out because they can't read something.
import { ElegantTooltip } from "../../../shared/formComponents/ElegantTooltip/ElegantTooltip";
// ⚠️ Bands and the stroke vocabulary now come from the SHARED graph adapter,
// not from a local mock. `WigConnectionsData.js` is deleted — the real §6a
// payload arrived on 2026-08-28, which is exactly the trigger D13 said would
// retire it. Bands are a PROP (see the signature below) because they are derived
// from the live snapshot's sections, so this component no longer knows the
// blueprint's shape at module load.
import { CONNECTION_SOURCE } from "../ExecutionBlueprintLiveReview/_shared/connectorsGraph";
import { canDeleteLiveReviewItem } from "../ExecutionBlueprintLiveReview/_shared/AddWigRow";
import { LIVE_REVIEW_SECTION_MAX_ITEMS } from "../ExecutionBlueprintLiveReview/_socket/LiveReviewSocketStore";

// ─────────────────────────────────────────────────────────────────────────────
// The interactive connection canvas.
//
// ── One click, both jobs ────────────────────────────────────────────────────
// Selecting a card does two things at once:
//
//   FOCUS   (33831:48775) its direct connections light up, the card and its
//           neighbours stay legible, everything unrelated fades back
//   CONNECT (33831:50013) its four connection points appear, ready to drag
//           from
//
// So a single click both answers "what does this WIG touch?" and arms the
// tool for changing it. An earlier version put connect mode behind an "Add
// connection" item in the card's ⋯ menu, which is a click and a decision
// before the affordance the user was already looking for appears - the points
// now come up with the selection instead.
//
// Clicking empty canvas returns to the plain view. The remaining states
// follow: 49961 drag (preview line), 49906 hover a target, 49806 drop
// (created, selection clears), 49855 select a line and drag an endpoint to
// reconnect. Delete removes a selected line.
//
// ── Why coordinates are measured, not stored ────────────────────────────────
// Nodes are laid out by CSS grid inside band rows, so the design's row/column
// structure lives in the stylesheet. The SVG overlay needs pixel anchors, so
// they're measured after layout - walking offsetLeft/offsetTop up to the
// canvas rather than using getBoundingClientRect, because offsets are
// unaffected by the zoom transform. Anchors therefore stay in unscaled canvas
// space and only pointer coordinates are divided by scale.
// ─────────────────────────────────────────────────────────────────────────────

// ── Where the stroke stops and the arrowhead starts ─────────────────────────
// The arrowhead is anchored at the BACK of the head, not near its tip, and the
// path is trimmed short by exactly the distance the head reaches forward of
// that anchor. That pairing is what stops the stroke showing through the head,
// and neither half works without the other.
//
// An earlier setup anchored the marker just behind its tip (`refX = 8.4`), so
// the triangle covered the last ~8px of stroke and the stroke's own end sat at
// the tip - where the triangle is zero pixels wide. A 1.4-2.2px stroke ending
// inside a zero-width point has nowhere to hide, so it squeezed out past the
// tip and past the sloped sides.
//
// Now: the stroke ends where the head's solid body begins, the head occupies
// the remaining distance, and its tip lands on the card edge. Nothing overlaps
// anything and nothing is left showing between them.
//
// ⚠ "The back of the head" is subtler than it sounds on a concave head - see
// the ARROW_NOTCH block below, which is where the anchor is actually decided.
//
// ARROW_W is the head's width across the line, and it constrains FAN_STEP: two
// heads arriving on the same card edge need more than ARROW_W between them or
// they merge into one blunt mass.
const ARROW_LEN = 13;
// 7.5, not the 9 this briefly was. The head's WIDTH sets the floor for
// FAN_STEP, so a wide head forces the connections on a card face apart - at 9px
// wide they needed 14px of step, which spread a card's bundle across a quarter
// of its edge and read as too much space between the lines. Narrowing the head
// is what buys the tighter bundle below; the LENGTH (13) is what carries the
// "bigger arrow", and it is unchanged. Net against the original 9 x 6.4 head
// this is still longer and marginally wider - just sharper.
const ARROW_W = 7.5;

// ── Why the head is anchored at its NOTCH, not at its back ──────────────────
// The head's back is concave (see the marker path), so along the line's own
// axis the solid body doesn't start at the back plane - it starts at the notch
// vertex, ARROW_NOTCH forward of it. The two barbs reach further back, but they
// sit out at the edges, away from the centreline the stroke runs down.
//
// Anchoring at the back plane therefore left the stroke ending ARROW_NOTCH
// short of any actual arrow, and the wedge between them read as a gap between
// the line and its arrowhead. It scaled with the head: 2.1px when the head was
// 9px long, 2.6px once it grew to 13px.
//
// So the marker is anchored at the notch vertex instead. The stroke's end now
// lands exactly on it, and the barbs flare back on either side of the last few
// pixels of stroke - which is how an arrowhead is supposed to meet its shaft.
//
// ⚠ The trim is consequently ARROW_TRIM, not ARROW_LEN. What has to be trimmed
// is the head's REACH FORWARD OF ITS ANCHOR, and the anchor is no longer the
// back. Trimming by the full length would push the tip ARROW_NOTCH past the
// card edge and into the card. All three constants move together: change the
// head's size or its notch depth and ARROW_TRIM follows automatically, which is
// why it is derived here rather than written out.
const ARROW_NOTCH = ARROW_LEN * 0.2;
const ARROW_TRIM = ARROW_LEN - ARROW_NOTCH;

const measureNode = (element, root) => {
  let x = 0;
  let y = 0;
  let node = element;
  while (node && node !== root) {
    x += node.offsetLeft;
    y += node.offsetTop;
    node = node.offsetParent;
  }
  return { x, y, w: element.offsetWidth, h: element.offsetHeight };
};

// ── Shared edge-midpoint anchors ────────────────────────────────────────────
// Every line leaves and arrives at the MIDPOINT of a card edge, never at a
// point computed per-target.
//
// That distinction is the whole behaviour: a card with two outgoing
// connections - say one to the card directly below and one to the card below
// and to the right - now has both lines leaving from the single centre point
// of its bottom edge, fanning out from there. Clipping the centre-to-centre
// line to the border (the earlier approach) gave each line its own exit point
// a few pixels apart, which made a card with several connections look like it
// had a frayed edge rather than a bundle leaving one place.
//
// Side is chosen by GRID POSITION, not by pixel distance:
//
//   target in a row below   → bottom centre
//   target in a row above   → top centre
//   target to the right, same row → right centre
//   target to the left, same row  → left centre
//
// Keying off the grid rather than the geometry matters. Measuring dx vs dy
// sounds equivalent but isn't: a card one row down and two columns across has
// a larger horizontal delta than vertical, so a distance test sends its line
// out of the SIDE of the card when it plainly belongs on the bottom. Rows are
// the structure here, so the row index decides the axis and the column index
// only breaks ties within a row.
const SIDE = { TOP: "top", RIGHT: "right", BOTTOM: "bottom", LEFT: "left" };
const AXIS = { V: "v", H: "h" };

// How far apart two cards must be in the same row before their line has to
// detour around the cards between them rather than run behind them.
const ADJACENT = 1;

// ⚠️ `GRID_COLUMNS` USED TO LIVE HERE AND IS DELETED (2026-09-15).
//
// It mirrored `.wcx-band-nodes`'s column count so the layout map could work out
// which visual row a card landed on once a band had more cards than columns.
// That grid is now `grid-auto-flow: column` — one track per card, no wrapping
// at any count — so there is no second row to work out and nothing left for the
// two files to disagree about.
//
// Do not reintroduce it without putting a fixed `grid-template-columns` back:
// the constant was only ever correct as a copy of that declaration.

const opposite = {
  [SIDE.TOP]: SIDE.BOTTOM,
  [SIDE.BOTTOM]: SIDE.TOP,
  [SIDE.LEFT]: SIDE.RIGHT,
  [SIDE.RIGHT]: SIDE.LEFT,
};

// `pad` pushes the arrival point clear of the card so the arrowhead sits just
// off the edge rather than overlapping the border. `slide` shifts the anchor
// ALONG the edge - see the fan-out section below for why.
const anchorAt = (rect, side, pad = 0, slide = 0) => {
  const cx = rect.x + rect.w / 2;
  const cy = rect.y + rect.h / 2;
  switch (side) {
    case SIDE.TOP:
      return { x: cx + slide, y: rect.y - pad };
    case SIDE.BOTTOM:
      return { x: cx + slide, y: rect.y + rect.h + pad };
    case SIDE.LEFT:
      return { x: rect.x - pad, y: cy + slide };
    default:
      return { x: rect.x + rect.w + pad, y: cy + slide };
  }
};

// ── Fanning several connections off one edge ────────────────────────────────
// Anchoring every connection at the exact edge midpoint put four lines through
// one pixel: their arrowheads landed on top of each other and the bundle read
// as a single thick smudge rather than four separate relationships.
//
// So connections sharing an edge slide along it - a few pixels each, ordered by
// where the other end actually is, so the leftmost target gets the leftmost
// exit point and lines never cross each other on their way out. They still
// read as one bundle leaving one face; they're just individually followable.
//
// The spread is clamped to the card's own extent so a card with many
// connections keeps its anchors on its edge instead of overhanging it.
//
// FAN_STEP is tied to ARROW_W, not chosen freely. Neighbouring connections on
// one face are sorted by where their other end is, so their arrows point in
// SIMILAR directions - which makes the real clearance test the perpendicular
// distance between two roughly PARALLEL heads:
//
//     clearance = FAN_STEP * cos(max arrival angle) - ARROW_W
//
// ⚠ The cosine term is not a rounding detail, it is most of the budget. Because
// `magnitude` pins to CURVE_MIN for adjacent bands, the lean clamp is reached
// once the two cards are only ~53px apart horizontally - and the column pitch
// is ~208px, so essentially EVERY non-stacked connection arrives at the maximum
// angle. Treat max lean as the normal case when sizing this, never as a corner.
//
// The bundle wants to be tight: a card's connections should read as leaving one
// place, and 14px of step spread them across a quarter of the card's edge, which
// looked like the lines had drifted apart rather than fanned. 10px is close to
// the 5-10px that was asked for originally, and it is the tightest step the head
// allows - at 33 degrees of lean it leaves ~0.9px between neighbouring heads,
// and 2.5px on a straight-down arrival.
const FAN_STEP = 10;
// The clamp only binds on a badly crowded face now that the step is tighter, so
// this mostly just delays the point where the step starts shrinking.
const FAN_EDGE_INSET = 10;

const fanOffsets = (count, extent) => {
  if (count <= 1) return [0];
  const limit = Math.max(extent / 2 - FAN_EDGE_INSET, 0);
  // Shrink the step rather than the count if the bundle would overhang: every
  // connection keeps a distinct anchor, they just sit closer together.
  const step = Math.min(FAN_STEP, (limit * 2) / (count - 1));
  const mid = (count - 1) / 2;
  return Array.from({ length: count }, (_, index) => (index - mid) * step);
};

// The drag preview picks its own side inline at the render site, since it also
// needs the chosen axis to bend the curve - see the preview path below.

// ── Curved connectors ───────────────────────────────────────────────────────
// A cubic bezier whose control points are pushed out along the DOMINANT axis of
// the connection. That axis test is the whole trick:
//
//   mostly vertical (the common case - one band driving the one above it) →
//     control points offset vertically, giving the S-curve the design draws:
//     the line leaves the card's top/bottom face perpendicular, bends, and
//     arrives perpendicular at the other.
//   mostly horizontal (two cards in the same row) → offset horizontally
//     instead, or the curve would loop out sideways and cross its own cards.
//
// Straight lines were the earlier version and read poorly at this density:
// eighteen straight strokes fanning through a five-row grid produce a lot of
// near-parallel diagonals that are hard to follow to their endpoint. A curve
// leaves and arrives square to the card, so the eye can pick up a line at a
// card edge and follow it.
//
// The 0.42 factor and the 26px floor are tuned rather than derived: too little
// curvature and it reads as a wobbly straight line, too much and short hops
// between adjacent rows balloon into semicircles. The floor is what keeps
// short connections curved at all.
const CURVE = 0.42;
const CURVE_MIN = 26;
// ...and a ceiling, which is what keeps a dense map readable. Without it a
// connection spanning three or four bands gets a control point 150px+ out, so
// it leaves its card, runs a long way straight down its own column, then makes
// a hard lateral traverse across the middle of the canvas. A dozen of those
// turn the centre of the map into a thicket. Capped, a long connection takes a
// more direct, lower-curvature route and crosses far less of the board. It only
// bites on connections skipping two or more rows - anything closer is already
// under the cap and its shape is unchanged.
const CURVE_MAX = 90;

// How much of the lateral distance the final control point pulls back toward
// the source. This is what makes the arrival ANGLED.
//
// The previous curve put the last control point directly above/below the
// endpoint - `C …, end.x, end.y - lift, end.x, end.y` - so the incoming
// tangent was exactly vertical no matter how far the line had travelled
// sideways. `orient="auto"` was doing its job perfectly; the curve simply
// arrived straight every time, which is why every arrowhead looked straight
// even on a strongly curved line.
//
// Offsetting that control point laterally gives the arrival a real direction,
// and the marker then follows it for free. At dx = 0 - two cards stacked
// exactly - the offset is zero and the arrow is straight down, which is what a
// straight line should look like.
const ARRIVAL_LEAN = 0.32;

// ...but the lean has to be BOUNDED, and this is the fix for arrowheads that
// looked wrong on long lateral connections.
//
// `dx * ARRIVAL_LEAN` is unbounded, so a card one row down and a long way
// across produced an enormous lateral pull against a small lift, and the line
// arrived at the card almost PARALLEL to the face it was entering. Measured:
// at dy = 40, dx = 800 the old arrival was 84° off perpendicular - an arrowhead
// lying flat along the top edge of the card it is supposed to point into,
// rather than pointing at it. Every value of dx above ~200 on a single-row hop
// was past 65°.
//
// Clamping the pull to a fraction of the lift ties the arrival angle to the
// curve's own shape: atan(0.65) means an arrival is never more than 33° off
// square. A stacked pair (dx = 0) still arrives at exactly 0°, so "straight
// line, straight arrow" is preserved, and a swept line still gets a visibly
// swept head - it just can't graze.
//
// It also does real work for density: every arrow landing on one card edge now
// comes in at a similar, near-square angle, so a bundle of arrivals reads as a
// bundle instead of a spray. That similarity is what the FAN_STEP clearance
// figure above relies on - and since a tighter bundle spends most of its budget
// on the cosine of this angle, the two constants have to be chosen together.
// This came down from 0.8 to pay for FAN_STEP dropping to 10.
const ARRIVAL_LEAN_RATIO = 0.65;

const clampLean = (value, limit) => Math.max(-limit, Math.min(limit, value));

// `axis` is passed in rather than inferred from the deltas. Once the anchor
// side is decided by grid position, the curve has to bend along the SAME axis
// the line leaves on - inferring it from dx/dy would give a diagonal
// connection horizontal control points while its anchors sit on top and bottom
// edges, which kinks the curve sideways out of the card face.
//
// The two control points are deliberately asymmetric: the line LEAVES its card
// square to the face (so a bundle of connections reads as coming out of one
// edge) and ARRIVES at an angle (so the arrowhead points along the sweep).
// Control points for a curve from `start` to `target`, on the given axis.
const controlPoints = (start, target, axis) => {
  const dx = target.x - start.x;
  const dy = target.y - start.y;
  if (axis === AXIS.V) {
    // `magnitude` is the unsigned reach of both control points. The lean is
    // clamped against it rather than against `dy`, so the arrival angle stays
    // tied to the curve's actual shape even once CURVE_MAX has capped it.
    const magnitude = Math.min(
      Math.max(Math.abs(dy) * CURVE, CURVE_MIN),
      CURVE_MAX,
    );
    const lift = magnitude * Math.sign(dy || 1);
    const lean = clampLean(dx * ARRIVAL_LEAN, magnitude * ARRIVAL_LEAN_RATIO);
    return [
      { x: start.x, y: start.y + lift },
      { x: target.x - lean, y: target.y - lift },
    ];
  }
  const magnitude = Math.min(
    Math.max(Math.abs(dx) * CURVE, CURVE_MIN),
    CURVE_MAX,
  );
  const push = magnitude * Math.sign(dx || 1);
  const lean = clampLean(dy * ARRIVAL_LEAN, magnitude * ARRIVAL_LEAN_RATIO);
  return [
    { x: start.x + push, y: start.y },
    { x: target.x - push, y: target.y - lean },
  ];
};

// Trims `trim` pixels off the arrival end, along the curve's own end tangent,
// and returns the shortened path plus that tangent.
//
// Two passes, because the trim and the tangent depend on each other: the first
// gets the tangent at the true edge point, the second rebuilds the curve to the
// pulled-back point so its control points suit the shorter path. One extra pass
// is plenty - a second iteration moves the result by well under a pixel.
//
// Trimming along the TANGENT rather than along the edge normal is what keeps
// the arrowhead's tip on the card edge no matter how angled the arrival is.
// Pulling straight back from the edge would leave an angled arrow short of the
// card by a gap that grows with the angle.
const trimmedCurve = (start, target, axis, trim) => {
  const [, firstC2] = controlPoints(start, target, axis);
  const tx = target.x - firstC2.x;
  const ty = target.y - firstC2.y;
  const len = Math.hypot(tx, ty) || 1;
  const ux = tx / len;
  const uy = ty / len;

  const end = { x: target.x - ux * trim, y: target.y - uy * trim };
  const [c1, c2] = controlPoints(start, end, axis);
  return {
    d: `M ${start.x} ${start.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${end.x} ${end.y}`,
    end,
  };
};

// ── Detour arc for distant same-row pairs ───────────────────────────────────
// Card 1 → card 5 in a five-card row would otherwise run straight through
// cards 2, 3 and 4. Lines sit behind cards, so that line would simply be
// invisible for most of its length.
//
// Instead it leaves the bottom centre, bows out below the row, and comes back
// up into the target's bottom centre - a shape that reads unmistakably as "these
// two, around everything between them", and stays entirely in the open space
// below the row where nothing can cover it.
//
// The dip scales with column distance so a 1→5 arc sits deeper than a 1→3 one
// and several detours in the same row nest instead of overlapping into an
// unreadable braid.
const DETOUR_BASE = 22;
const DETOUR_PER_COLUMN = 11;

// ── Reorder drag ────────────────────────────────────────────────────────────
// How far the pointer must travel before a press on a card becomes a drag
// rather than a click.
//
// It has to exist at all because the card body is BOTH the select target and the
// drag handle. Without a threshold every selection would nudge the card a pixel
// and register as a reorder, and the focus-a-card-to-trace-its-connections
// gesture — the thing this screen is mostly used for — would become unreliable.
//
// 5px is the usual figure for a mouse: high enough to absorb the tremor in a
// deliberate click, low enough that an intended drag starts before the user
// wonders whether the card is stuck.
const REORDER_THRESHOLD = 5;

/**
 * Move `id` to `toIndex` within `ids`, returning a new array.
 *
 * Written out rather than done inline because the off-by-one is real: after
 * splicing the dragged id OUT, every index above its old position has shifted
 * down by one, so a naive `splice(toIndex, 0, id)` drops the card one place
 * short whenever it moves right. Removing first and clamping against the
 * SHORTENED array is what makes "drag to the end" actually reach the end.
 */
const moveWithin = (ids, id, toIndex) => {
  const from = ids.indexOf(id);
  if (from === -1) return ids;
  const next = ids.slice();
  next.splice(from, 1);
  const clamped = Math.max(0, Math.min(toIndex, next.length));
  next.splice(clamped, 0, id);
  return next;
};

// Trimmed the same way as curvePath, so a detour's arrowhead gets the same
// clean handoff from stroke to head. Its arrival is straight up out of the arc,
// so the trim is a simple vertical pullback.
const detourPath = (start, target, columnDistance, trim) => {
  const dip = DETOUR_BASE + columnDistance * DETOUR_PER_COLUMN;
  const end = { x: target.x, y: target.y + trim };
  return {
    d: `M ${start.x} ${start.y} C ${start.x} ${start.y + dip}, ${end.x} ${
      end.y + dip
    }, ${end.x} ${end.y}`,
    end,
  };
};

// Which face each end of a connection uses, and how it should be drawn. Pure
// classification - no coordinates yet, because the anchors can't be placed
// until every connection sharing a face is known (see buildGeometry).
const classify = (fromMeta, toMeta) => {
  // Missing metadata shouldn't blank a line out; fall back to a vertical
  // connector, which is the overwhelmingly common case on this map.
  if (!fromMeta || !toMeta) {
    return {
      fromSide: SIDE.BOTTOM,
      toSide: SIDE.TOP,
      axis: AXIS.V,
      detour: false,
    };
  }

  // Different row → vertical. The row index decides this, not the pixel
  // delta, so a card one row down and several columns across still leaves
  // through the bottom face.
  if (fromMeta.rowIndex !== toMeta.rowIndex) {
    const fromSide =
      toMeta.rowIndex > fromMeta.rowIndex ? SIDE.BOTTOM : SIDE.TOP;
    // ⚠️ A CONNECTION SPANNING TWO OR MORE BANDS IS DRAWN THE SAME WAY as any
    // other cross-row one, and that is a deliberate call rather than an
    // oversight. A cubic's midpoint is (P0 + 3P1 + 3P2 + P3) / 8, and here the
    // control points' vertical reach cancels, so the halfway point is pinned to
    // (y0 + y1) / 2 — which for a two-band span lands inside the intervening
    // band's card row, and lines paint under cards. Part of such a line is
    // therefore covered.
    //
    // An axis-aligned route through the gutters was built to avoid that
    // (2026-09-15) and REVERTED the same day: it fixed the occlusion and looked
    // like a circuit trace next to every other line on the map. On a canvas
    // whose whole visual language is flowing curves, a few partly-covered
    // curves read better than a few right-angled ones. If this is revisited,
    // the shape has to stay curved — the tension is that avoiding cards wants
    // steep local crossings while looking curved wants long gentle ones.
    return {
      fromSide,
      toSide: opposite[fromSide],
      axis: AXIS.V,
      detour: false,
    };
  }

  const columnDistance = Math.abs(toMeta.colIndex - fromMeta.colIndex);

  // Same row, side by side → straight across through the gap between them.
  if (columnDistance <= ADJACENT) {
    const fromSide =
      toMeta.colIndex > fromMeta.colIndex ? SIDE.RIGHT : SIDE.LEFT;
    return {
      fromSide,
      toSide: opposite[fromSide],
      axis: AXIS.H,
      detour: false,
      columnDistance,
    };
  }

  // Same row, cards in between → arc below the row instead of behind them.
  return {
    fromSide: SIDE.BOTTOM,
    toSide: SIDE.BOTTOM,
    axis: AXIS.V,
    detour: true,
    columnDistance,
  };
};

const centreOf = (rect) => ({
  x: rect.x + rect.w / 2,
  y: rect.y + rect.h / 2,
});

// Builds every connection's path in one pass, because the fan offsets can only
// be worked out once all the connections sharing a card face are known.
//
// Two passes: classify each connection and bucket its two ends by
// `nodeId:side`, then order each bucket and hand out offsets. Ordering by the
// OTHER end's position along the edge axis is what stops the fanned lines
// crossing each other as they leave - the leftmost target gets the leftmost
// anchor.
const buildGeometry = (connections, rects, layout) => {
  const result = {};
  const buckets = {};
  const classified = [];

  connections.forEach((connection) => {
    const fromRect = rects[connection.from];
    const toRect = rects[connection.to];
    if (!fromRect || !toRect) return;

    const spec = classify(layout[connection.from], layout[connection.to]);
    classified.push({ connection, spec, fromRect, toRect });

    const push = (nodeId, side, sortKey) => {
      const key = `${nodeId}:${side}`;
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push({ id: connection.id, sortKey });
    };

    // Sort key is the other end's position along the axis this face fans on:
    // horizontal for a top/bottom face, vertical for a left/right one.
    const fromHorizontal =
      spec.fromSide === SIDE.TOP || spec.fromSide === SIDE.BOTTOM;
    const toHorizontal =
      spec.toSide === SIDE.TOP || spec.toSide === SIDE.BOTTOM;
    push(
      connection.from,
      spec.fromSide,
      fromHorizontal ? centreOf(toRect).x : centreOf(toRect).y,
    );
    push(
      connection.to,
      spec.toSide,
      toHorizontal ? centreOf(fromRect).x : centreOf(fromRect).y,
    );
  });

  // connectionId:end → offset along the edge
  const slides = {};
  Object.entries(buckets).forEach(([key, entries]) => {
    const [nodeId, side] = key.split(":");
    const rect = rects[nodeId];
    if (!rect) return;
    const horizontal = side === SIDE.TOP || side === SIDE.BOTTOM;
    const extent = horizontal ? rect.w : rect.h;
    const ordered = [...entries].sort((a, b) => a.sortKey - b.sortKey);
    const offsets = fanOffsets(ordered.length, extent);
    ordered.forEach((entry, index) => {
      slides[`${entry.id}:${key}`] = offsets[index];
    });
  });

  classified.forEach(({ connection, spec, fromRect, toRect }) => {
    const startSlide =
      slides[`${connection.id}:${connection.from}:${spec.fromSide}`] || 0;
    const endSlide =
      slides[`${connection.id}:${connection.to}:${spec.toSide}`] || 0;

    const start = anchorAt(fromRect, spec.fromSide, 0, startSlide);
    // `target` is the point on the card edge the arrowhead's TIP should reach;
    // the path itself stops ARROW_TRIM short of it.
    const target = anchorAt(toRect, spec.toSide, 0, endSlide);

    const { d } = spec.detour
      ? detourPath(start, target, spec.columnDistance, ARROW_TRIM)
      : trimmedCurve(start, target, spec.axis, ARROW_TRIM);

    result[connection.id] = {
      start,
      // The endpoint HANDLE goes on the card edge, not at the trimmed path
      // end - it's a grab target for reconnecting, and it should sit where the
      // connection visually terminates rather than floating ARROW_TRIM short of
      // it. `end` is therefore the edge point; the trimmed path end is inside
      // `d` and nothing else needs it.
      end: target,
      detour: spec.detour,
      d,
    };
  });

  return result;
};

const CONNECTION_POINTS = ["top", "right", "bottom", "left"];

// ⚠️ `NodeMoreIcon` was deleted here on 2026-09-03 with the rest of the card
// header. It drew the ⋯ affordance, which had carried no menu since
// add-connection moved onto card selection — so nothing is lost behind it. If
// per-WIG actions (rename, remove) ever land, this is where the mark went.

// ─── The goal's wording, with the full text on hover when it doesn't fit ────
// `.wcx-node-value` is clamped to three lines (see WigConnections.scss for why
// the card is not allowed to grow), so a longer goal ends in an ellipsis and
// the rest of it was simply unreachable — on the editable map and the read-only
// one alike. The tooltip is the way back to it.
//
// ⚠️ ONLY WHEN IT IS ACTUALLY CLIPPED. A map of eighteen cards where every
// hover pops a tooltip repeating text already on screen is worse than no
// tooltip, especially since hovering is how you trace a line across this
// screen. `scrollHeight > clientHeight` is the test, and it works under the
// zoom transform because both are LAYOUT values — a scaled box reports its
// unscaled size.
//
// Measured in a layout effect rather than on hover: `disabled` is read at
// render time by ElegantTooltip, so deciding on mouseenter would mean the
// tooltip mounts after the event that was supposed to open it and the first
// hover does nothing.
const NodeValue = ({ value, suppressed }) => {
  const ref = useRef(null);
  const [clipped, setClipped] = useState(false);

  useLayoutEffect(() => {
    const measure = () => {
      const el = ref.current;
      // 1px of slack: sub-pixel line heights make an unclipped element report
      // a scrollHeight a fraction taller than its clientHeight, which would
      // put a tooltip on every card on the map.
      if (el) setClipped(el.scrollHeight - el.clientHeight > 1);
    };
    measure();
    // Re-measured once the webfont lands. Measuring against the fallback font
    // answers a different question than the one the user sees — a fallback
    // that sets wider than the real face reads as clipped when it is not, and
    // narrower reads as unclipped when it is.
    const fonts = typeof document !== "undefined" ? document.fonts : null;
    if (!fonts?.ready) return undefined;
    let live = true;
    fonts.ready.then(() => {
      if (live) measure();
    });
    return () => {
      live = false;
    };
  }, [value]);

  return (
    <ElegantTooltip
      content={value}
      placement="top"
      maxWidth={280}
      // Never mid-gesture. While a connection is being dragged or a card
      // carried, the pointer is over cards it is passing THROUGH, and a
      // tooltip there covers the very cards the user is aiming at.
      disabled={suppressed || !clipped}
    >
      <p className="wcx-node-value" ref={ref}>
        {value}
      </p>
    </ElegantTooltip>
  );
};

const WigNode = ({
  node,
  focused,
  neighbour,
  faded,
  isTarget,
  // True when `isTarget` is set but the drop would be refused — see
  // `rejectionFor`. Split from `isTarget` rather than folded into it because
  // the card still has to show it is the hovered one; only the verdict differs.
  targetInvalid = false,
  dimmed,
  connectMode,
  onSelect,
  onPointDown,
  registerRef,
  readOnly = false,
  // ── Reorder ──
  bandKey,
  lifted = false,
  reorderPeer = false,
  onBeginPress,
  onEndPress,
  // Absent unless this card may actually be removed — see the button below.
  onRemove,
  // True while a drag is in flight, so the value tooltip stays out of the way.
  tooltipsSuppressed = false,
}) => (
  <div
    className={[
      "wcx-node",
      `wcx-node--${node.band}`,
      focused ? "is-focused" : "",
      neighbour ? "is-neighbour" : "",
      faded ? "is-faded" : "",
      // Draggable at all — drives the grab cursor, which is the only standing
      // hint that these cards move. Absent when read-only, so the locked map
      // never advertises a gesture it won't honour, and absent on BHAG, which
      // is not a section and cannot be reordered (see `beginPress`).
      !readOnly && bandKey !== "bhag" ? "is-draggable" : "",
      lifted ? "is-lifted" : "",
      reorderPeer ? "is-reorder-peer" : "",
      // Two target states, not one: a card that will accept the drop and a
      // card that will refuse it. Both are "the thing under the cursor", so
      // both need a ring — but a blue one on a card that is about to reject
      // the drop is an invitation to release.
      isTarget && !targetInvalid ? "is-target" : "",
      isTarget && targetInvalid ? "is-target-invalid" : "",
      dimmed ? "is-dimmed" : "",
    ]
      .filter(Boolean)
      .join(" ")}
    ref={(element) => registerRef(node.id, element)}
    data-wig-id={node.id}
    // ⚠️ NO `style` FOR THE LIFTED CARD. Its transform is written straight to
    // the DOM node by the mousemove handler — see the note there. Passing it as
    // a prop meant a full canvas re-render per pointer move, which is what made
    // the drag lag. React must not also control this property, or the two
    // writers fight and the card stutters between them.
    onMouseDown={(event) => {
      // Stops the canvas's own mousedown from clearing what this click is
      // about to select.
      event.stopPropagation();
      // Arms the reorder. Selection has MOVED TO MOUSEUP (2026-09-14) and that
      // move is the whole reason a drag is possible: the card body is both the
      // select target and the drag handle, so selecting here would fire on the
      // first pixel of every drag — focusing a card, fading its neighbours, and
      // leaving the user dragging through a row they can no longer read.
      if (onBeginPress) onBeginPress(node.id, bandKey, event);
    }}
    // ⚠️ SELECT ON MOUSEUP, AND THE DECISION IS THE PARENT'S. Two different
    // presses end on a card and only one of them is a click:
    //
    //   • pressed here, released here, never moved   → select
    //   • pressed on ANOTHER card, dragged, released over this one → do nothing
    //
    // The second is why `lifted` can't be the test: the card being carried is
    // the other one, so this card looks perfectly clickable from here. Only the
    // parent knows which card the press started on, so it answers.
    onMouseUp={() => onEndPress && onEndPress(node.id)}
    role="button"
    tabIndex={0}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onSelect(node.id);
      }
    }}
  >
    {/* ⚠️ NO CARD HEADER (2026-09-03, on instruction). `.wcx-node-head` held
        the band icon, the WIG's code (F1, C2, O3…) and the ⋯ dots, and all
        three went with it — on the live screen and the locked one alike.

        What that costs, so it is a decision on the record rather than a
        surprise later: the codes are gone from the map entirely, and they are
        the vocabulary everything else uses to refer to a WIG — the Connection
        Overview panel's "C1 · Retention Target", the Year 1 plan's rows, the
        agenda. A card is identified by its label and its band alone now, so
        two WIGs sharing a label (there is already a "Retention Target" in both
        the Customer and People bands) are told apart only by which row they
        sit in.

        The ⋯ was the one part with nothing behind it: its menu was emptied
        when add-connection moved onto card selection, so it had been an inert
        affordance for a while. */}
    <p className="wcx-node-label">{node.label}</p>
    {/* The goal's own wording under its name. Rendered only when present —
        BHAG has no `value`, and an item may legitimately have an empty one, in
        which case the card stays a single line rather than growing an empty
        row. */}
    {node.value ? (
      <NodeValue value={node.value} suppressed={tooltipsSuppressed} />
    ) : null}

    {/* Points belong to the selected card only. On every card they would turn
        the map into a field of handles and make the selection meaningless.
        Hidden mid-drag so the source card's own points don't sit under the
        line being dragged out of it. */}
    {/* ── Remove, on the selected card only ──
        Same rule as the connection points above and for the same reason: on
        every card at once it would be a field of delete buttons over a map
        people are reading, and on a map you read far more than you edit.
        Selecting a card is already the "I am working on this one" gesture.

        Absent when read-only and when the section is down to its last WIG —
        `onRemove` is simply not passed in either case, so there is one place
        that decides and no second opinion here. */}
    {focused && !connectMode && !readOnly && onRemove && (
      <button
        type="button"
        className="wcx-node-remove"
        title="Remove this WIG"
        aria-label={`Remove ${node.label}`}
        onMouseDown={(event) => {
          // Both stopped: mousedown on the card body arms the reorder drag and
          // also selects, and either would fire before the click lands.
          event.stopPropagation();
          event.preventDefault();
        }}
        onClick={(event) => {
          event.stopPropagation();
          onRemove(node.id);
        }}
      >
        ×
      </button>
    )}

    {focused && !connectMode && !readOnly && (
      <>
        {CONNECTION_POINTS.map((side) => (
          /* ⚠️ A pointer-only drag source, and knowingly so. Drawing a
             connection means pressing on one card's point and releasing over
             another; there is no keyboard equivalent of that gesture in this
             design, and giving the span a role plus a tab stop that does nothing
             on Enter would advertise an affordance a keyboard user cannot
             complete — worse than not offering it. The CEO repo's eslint config
             raises this to an error where this one warns, so it is disabled
             explicitly rather than left to differ between the two copies.
             **Keyboard-accessible connector editing is a real gap**, recorded as
             such rather than papered over here. */
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          <span
            key={side}
            className={`wcx-point wcx-point--${side}`}
            onMouseDown={(event) => {
              event.stopPropagation();
              event.preventDefault();
              onPointDown(node.id, event);
            }}
          />
        ))}
      </>
    )}
  </div>
);

const WigConnectionsCanvas = ({
  nodes,
  connections,
  // Was the module-level `bands`. Now a prop, because the band list is built
  // from the live snapshot's sections — BHAG first, then the four WIG sections
  // in the server's own `sequence` order. Defaulted to empty so a mount before
  // the graph lands renders nothing rather than throwing.
  bands = [],
  scale = 1,
  onCreateConnection,
  onUpdateConnection,
  onDeleteConnection,
  onSelectionChange,
  // ⚠️ Read-only mode, for the post-lock view screens (§6b.7 — once the terminal
  // lock fires, "treat the screen as fully read-only"). It removes the
  // AFFORDANCES, not just the handlers: no connection points, no line selection,
  // no Delete key. Passing no-op callbacks instead would leave a canvas that
  // still invites dragging and then silently ignores it, which is worse than a
  // canvas that plainly doesn't.
  readOnly = false,
  // ── Reordering WIGs within a band (2026-09-14) ────────────────────────────
  // Called on drop with `(bandKey, orderedIds)` — the band's FULL id list in its
  // new order, not a delta. A full list because the consumer's job is to render
  // that order, and reconstructing it from `{ id, from, to }` at every call site
  // is the kind of arithmetic that goes wrong once and then goes wrong quietly.
  //
  // ⚠️ NO BACKEND BEHIND THIS YET. The event that persists a reorder is coming;
  // until it does, the screens hold the order locally (see `orderOverrides` in
  // WigConnections.js) and it is not shared with the other person in the
  // session. When the socket event lands, this callback is where it hooks in —
  // nothing in this file should need to change.
  //
  // Omitted entirely by the locked screens, which pass `readOnly` instead. The
  // gesture is gated on `readOnly`, not on this being absent, so the affordance
  // and the handler can never disagree.
  onReorderNodes,
  // ── Adding and removing WIGs from the map (handoff #25 §2) ────────────────
  // Both omitted by the locked screens, which render this canvas through
  // `LockedBlueprintView`. `readOnly` already hides every other affordance;
  // these two are additionally absent, so a locked map cannot offer them even
  // if that flag were ever passed wrongly.
  //
  // `onAddRequest(sectionKey)` — the canvas only ASKS; the screen owns the
  // dialog and the emit. This component draws a map and has no business holding
  // a form's state.
  //
  // `onRemoveItem(nodeId)`.
  onAddRequest,
  onRemoveItem,
  onNaturalSize,
  // ⚠️ The SCROLL CONTAINER, handed back to whoever mounts this (2026-09-03).
  // The screen's Fit control has to measure the width actually available to
  // the map, and that width belongs to `.wcx-scroll` — which this component
  // owns. It used to be found with
  // `document.querySelector("#wig-connections .wcx-scroll")`: it worked, but
  // it reached across the whole document for a node rendered right here, and
  // it silently tied the fit maths to an id set on a different screen. Passing
  // the ref makes the dependency explicit and survives the panel being
  // reused.
  scrollRef,
}) => {
  const canvasRef = useRef(null);
  const nodeRefs = useRef({});
  const [rects, setRects] = useState({});
  const [size, setSize] = useState({ w: 0, h: 0 });

  // One id covers both jobs now - see the header note. A focused node shows
  // its connections highlighted AND its connection points.
  const [focusedNodeId, setFocusedNodeId] = useState(null);
  const [selectedConnectionId, setSelectedConnectionId] = useState(null);
  const [drag, setDrag] = useState(null);
  const [hoverTargetId, setHoverTargetId] = useState(null);
  const [flash, setFlash] = useState("");

  // ── Reordering a band ─────────────────────────────────────────────────────
  // `{ nodeId, band, from, to, cursor, origin }` while a card is being dragged
  // to a new position in its own row, else null.
  //
  // ⚠️ A SEPARATE STATE FROM `drag`, ON PURPOSE. `drag` means "a connector is
  // being drawn" and `connectMode` is derived from it — it dims every card, mutes
  // every line, hides the connection points and arms `handleUp` to create or
  // reconnect. A reorder is none of those things, and folding it in as a third
  // `mode` would mean auditing every `if (drag)` in this file for whether it
  // meant "connector drag" or "any drag". The two gestures are also mutually
  // exclusive by construction: a connector drag starts on a connection point,
  // which only the focused card shows, and `beginReorder` bails if `drag` is set.
  const [reorder, setReorder] = useState(null);

  // Where the pointer went down on a card, before we know whether it's a click
  // or a drag. A ref, not state: it changes on mousedown and is read on
  // mousemove, and re-rendering the whole canvas on mousedown to store a
  // coordinate would be a wasted pass on a map with a hundred cards.
  const pressRef = useRef(null);

  // Card positions captured just before a reorder, for the FLIP below.
  const flipRef = useRef(null);

  const registerRef = useCallback((id, element) => {
    if (element) nodeRefs.current[id] = element;
    else delete nodeRefs.current[id];
  }, []);

  const measure = useCallback(() => {
    const root = canvasRef.current;
    if (!root) return;
    const next = {};
    Object.entries(nodeRefs.current).forEach(([id, element]) => {
      if (element) next[id] = measureNode(element, root);
    });
    setRects(next);
    // Natural (unscaled) size - transforms don't affect offsetWidth, which is
    // exactly why the zoom wrapper can be sized from it.
    const natural = { w: root.offsetWidth, h: root.offsetHeight };
    setSize(natural);
    if (onNaturalSize) onNaturalSize(natural);
  }, [onNaturalSize]);

  // ⚠️ `reorder` IS DELIBERATELY NOT A DEPENDENCY HERE. It was, briefly, and it
  // was the single biggest cause of the drag feeling laggy: every slot change
  // re-measured every card's bounding rect mid-gesture, for connectors that are
  // faded and unread for the whole drag. The drop calls `measure()` once,
  // directly — see `handleUp`.
  useLayoutEffect(() => {
    measure();
  }, [measure, nodes, connections]);

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    if (canvasRef.current) observer.observe(canvasRef.current);
    return () => observer.disconnect();
  }, [measure]);

  useEffect(() => {
    if (onSelectionChange) onSelectionChange(focusedNodeId);
  }, [focusedNodeId, onSelectionChange]);

  // ── Grid position per node ────────────────────────────────────────────────
  // The row/column index each node sits at, which is what the anchor rule and
  // the detour test both key off. Derived from bands' order and each
  // node's position within its band, so it stays correct if the data changes
  // without needing anything measured.
  //
  // VISUAL rows, not bands. A band with more nodes than the grid has columns
  // wraps onto a second line, and those two lines are different rows as far as
  // the connector rules are concerned - otherwise a vertically stacked pair
  // inside one band would be treated as same-row and get a detour arc looping
  // below them, and their anchors would come out of the left and right faces
  // instead of top and bottom. Today's bands are 1/4/4/4/5, so nothing wraps
  // yet and this is protection rather than a fix - but a sixth People WIG
  // would have hit it.
  // ⚠️ ONE VISUAL ROW PER BAND, ALWAYS. `.wcx-band-nodes` is
  // `grid-auto-flow: column`, so a track is created per card and a band cannot
  // wrap at any count — the row/column arithmetic that used to divide by
  // `GRID_COLUMNS` is gone with it, along with the coupling between this file
  // and the stylesheet's column count.
  //
  // What the map still has to say is exactly what the connector geometry reads:
  // which visual row a card is in (bands stack, so it is the band's index) and
  // how far apart two cards are along it (their position in the band).
  const layout = useMemo(() => {
    const map = {};
    bands.forEach((band, rowIndex) => {
      nodes
        .filter((node) => node.band === band.key)
        .forEach((node, colIndex) => {
          map[node.id] = { rowIndex, colIndex };
        });
    });
    return map;
  }, [nodes, bands]);

  // Every connection's path, built in one pass so connections sharing a card
  // face can be fanned relative to each other.
  const geometry = useMemo(
    () => buildGeometry(connections, rects, layout),
    [connections, rects, layout],
  );

  // ── Focus set ─────────────────────────────────────────────────────────────
  // The connections touching the focused node, and the nodes at their far
  // ends. Everything else fades. Computed here rather than per-node so each
  // node and line is a single Set lookup during render.
  const focusSet = useMemo(() => {
    if (!focusedNodeId) return null;
    const nodeIds = new Set([focusedNodeId]);
    const connectionIds = new Set();
    connections.forEach((connection) => {
      if (connection.from === focusedNodeId) {
        nodeIds.add(connection.to);
        connectionIds.add(connection.id);
      } else if (connection.to === focusedNodeId) {
        nodeIds.add(connection.from);
        connectionIds.add(connection.id);
      }
    });
    return { nodeIds, connectionIds };
  }, [focusedNodeId, connections]);

  const toCanvasPoint = useCallback(
    (event) => {
      const root = canvasRef.current;
      if (!root) return { x: 0, y: 0 };
      const box = root.getBoundingClientRect();
      return {
        x: (event.clientX - box.left) / scale,
        y: (event.clientY - box.top) / scale,
      };
    },
    [scale],
  );

  const nodeIdAtPoint = (event) => {
    const element = document.elementFromPoint(event.clientX, event.clientY);
    const card = element && element.closest("[data-wig-id]");
    return card ? card.getAttribute("data-wig-id") : null;
  };

  // ── Would dropping on this card be refused, and why? ──────────────────────
  // ⚠️ ONE RULE, TWO READERS (2026-09-03). This is deliberately the single
  // source of truth for "this drop will not connect", consulted by BOTH the
  // live drag feedback (the red preview line, the red target ring, the hint)
  // and the drop handler itself.
  //
  // It exists because the alternative was already written and was a bug waiting
  // to happen: the checks used to live inline in `handleUp`, so showing them
  // during the drag would have meant a second copy of the same conditions. Two
  // copies of "is this a duplicate" drift, and when they drift the failure is
  // the worst kind — a line that turns red and then connects anyway, or one
  // that looks fine and is silently refused on release.
  //
  // Returns the REASON as a string, or null when the drop would succeed. A
  // string rather than a boolean because the hint shows it verbatim, so the
  // reason travels with the verdict rather than being re-derived from it.
  const rejectionFor = useCallback(
    (targetId) => {
      // Not rejections — there is simply nothing to drop on, so the preview
      // stays blue. Three cases collapsed into one guard, and the third is the
      // interesting one:
      //
      //   - no drag in flight, or the cursor is over empty canvas
      //   - `targetId === drag.fromId` on a CREATE drag: the source card. You
      //     are always over it at the moment you pick up its handle, so
      //     reddening it there would fire before the user has tried anything.
      //     `handleMove` never makes it the hover target for the same reason.
      //   - `targetId === drag.fromId` on a RECONNECT drag: `fromId` is the
      //     ANCHOR — the end that is NOT moving (see `handleEndpointDown`). So
      //     this is dropping the dragged endpoint back where it started, which
      //     is the natural cancel gesture. Doing nothing quietly is right;
      //     an error for "I changed my mind" would be hostile.
      //
      // This matches what the pre-2026-09-03 `handleUp` did — its
      // `valid = targetId && targetId !== drag.fromId` test gated both branches
      // the same way, so no behaviour changed when the checks moved in here.
      if (!drag || !targetId || targetId === drag.fromId) return null;

      if (drag.mode === "create") {
        return connections.some(
          (connection) =>
            connection.from === drag.fromId && connection.to === targetId,
        )
          ? "Those WIGs are already connected"
          : null;
      }

      // Reconnecting has to be checked against every OTHER connection, not
      // just the pair being dragged. The case that breaks a naive check:
      // card 1 already connects to both card 5 and card 6, and the user
      // drags the 1→5 line's endpoint onto card 6. Nothing about that drag
      // is self-referential, and the connection being moved isn't itself a
      // duplicate - but the result would be two identical 1→6 connections,
      // silently, with one drawn exactly on top of the other and no way to
      // tell them apart or delete the right one. Excluding the dragged
      // connection by id is what makes "drop it back where it came from" a
      // no-op rather than a false collision.
      const moving = connections.find(
        (connection) => connection.id === drag.connectionId,
      );
      const nextFrom = drag.end === "from" ? targetId : moving?.from;
      const nextTo = drag.end === "to" ? targetId : moving?.to;

      // ⚠️ CURRENTLY UNREACHABLE, and kept deliberately. `nextFrom === nextTo`
      // requires the moved endpoint to land on the stationary one — which is
      // `drag.fromId`, already returned null by the guard above. It was
      // unreachable before this function existed too; moving the checks here
      // is what made that visible.
      //
      // Not deleted, because it is the correct guard for the invariant rather
      // than a guess: if the guard above is ever narrowed (say, to redden the
      // anchor card on a reconnect), this becomes live immediately and is the
      // right answer when it does. A self-connection must never reach the
      // server — §8 rejects it as `invalid_connector`.
      if (nextFrom === nextTo) return "A WIG can't connect to itself";

      return connections.some(
        (connection) =>
          connection.id !== drag.connectionId &&
          connection.from === nextFrom &&
          connection.to === nextTo,
      )
        ? "Those WIGs are already connected"
        : null;
    },
    [drag, connections],
  );

  // The verdict for whatever the cursor is currently over. Drives every piece
  // of red on the canvas mid-drag.
  const hoverRejection = rejectionFor(hoverTargetId);

  useEffect(() => {
    if (!drag) return undefined;

    const handleMove = (event) => {
      setDrag((prev) =>
        prev ? { ...prev, cursor: toCanvasPoint(event) } : prev,
      );
      const overId = nodeIdAtPoint(event);
      setHoverTargetId(overId && overId !== drag.fromId ? overId : null);
    };

    const handleUp = (event) => {
      const targetId = nodeIdAtPoint(event);
      const valid = targetId && targetId !== drag.fromId;
      // Same call the preview made on the way in, so what the user was shown
      // is what actually happens.
      const rejection = valid ? rejectionFor(targetId) : null;

      if (valid && rejection) {
        setFlash(rejection);
      } else if (valid && drag.mode === "create") {
        if (readOnly) return;
        onCreateConnection(drag.fromId, targetId);
        setFlash("Connection created successfully");
      } else if (valid && drag.mode === "reconnect") {
        // Same guard the `create` branch above carries. Unreachable while
        // read-only blocks selection (a reconnect drag starts from a selected
        // line's endpoint), but a mutation call is not the place to rely on
        // that.
        if (readOnly) return;
        onUpdateConnection(drag.connectionId, drag.end, targetId);
        setFlash("Connection updated");
      }

      // Connect mode exits on drop whatever the outcome, so the canvas is
      // never left half-armed for the user to click out of.
      setDrag(null);
      setHoverTargetId(null);
      setFocusedNodeId(null);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [
    drag,
    connections,
    readOnly,
    onCreateConnection,
    onUpdateConnection,
    toCanvasPoint,
    rejectionFor,
  ]);

  // ── Where would the dragged card land? ────────────────────────────────────
  // Reads the LIVE rects of the band's cards and returns the index the dragged
  // card should occupy, given where the pointer is.
  //
  // Measured off the DOM rather than computed from the grid, because the grid
  // wraps: `.wcx-band-nodes` is `repeat(5, …)`, so a band with six WIGs is two
  // visual lines and any arithmetic on a single row would put the sixth card in
  // the wrong place. The rects always tell the truth about where things are.
  //
  // The comparison is against each card's CENTRE, and the dragged card is
  // skipped: it's the one under the cursor, so including it would make every
  // position adjacent to itself and the index would stick.
  const dropIndexAt = useCallback((bandKey, draggedId, clientX, clientY) => {
    const root = canvasRef.current;
    if (!root) return null;
    const row = root.querySelector(`[data-band-nodes="${bandKey}"]`);
    if (!row) return null;

    const cards = Array.from(row.querySelectorAll("[data-wig-id]"));
    let index = 0;
    cards.forEach((card) => {
      if (card.getAttribute("data-wig-id") === draggedId) return;
      const box = card.getBoundingClientRect();
      // Wrapped rows mean "after" is two questions, not one: past this card's
      // centre horizontally, OR on a line below it entirely. Without the second
      // test, dragging down onto the second line of a wrapped band would keep
      // resolving to an index on the first line.
      const belowLine = clientY > box.bottom;
      const pastCentre =
        clientY >= box.top && clientX > box.left + box.width / 2;
      if (belowLine || pastCentre) index += 1;
    });
    return index;
  }, []);

  // ── FLIP: make the peers slide instead of teleport ────────────────────────
  // Reordering grid items moves them by re-running layout, and layout position
  // is not animatable — a `transition: transform` in the stylesheet looks
  // right in review and does nothing. FLIP is the standard way round it, in
  // four beats: First (measure), Last (let it re-render), Invert (transform it
  // back to where it was, with no transition), Play (release the transform with
  // one).
  //
  // `captureRects` is the First. It runs in the mousemove handler, immediately
  // before the state change that reorders the row, because that is the last
  // moment the old positions still exist.
  const captureRects = useCallback((bandKey) => {
    const root = canvasRef.current;
    if (!root) return null;
    const row = root.querySelector(`[data-band-nodes="${bandKey}"]`);
    if (!row) return null;
    const map = new Map();
    row.querySelectorAll("[data-wig-id]").forEach((card) => {
      map.set(card.getAttribute("data-wig-id"), card.getBoundingClientRect());
    });
    return map;
  }, []);

  // Invert + Play. A layout effect, so the inverting transform is applied in
  // the same frame as the re-render — in a plain effect the browser would paint
  // the cards in their new places first and the animation would run backwards
  // from a position the user already saw.
  useLayoutEffect(() => {
    const before = flipRef.current;
    flipRef.current = null;
    if (!before || !reorder) return;

    // ⚠️ REDUCED MOTION IS CHECKED HERE, NOT IN THE STYLESHEET. This animation
    // is written as inline styles, so the `@media (prefers-reduced-motion)`
    // block in WigConnections.scss — which zeroes `.wcx-node`'s transition —
    // loses to them on specificity. Someone who has asked for less motion gets
    // the instant reorder, which is still perfectly legible: the gap opens
    // either way.
    //
    // ⚠️ IT GUARDS ONLY THE PEERS' ANIMATION, and it used to `return` out of
    // the whole effect — which took the carried card's re-baselining with it,
    // so anyone with reduced motion still got the flick this effect exists to
    // remove. Re-baselining is correctness, not decoration: it happens either
    // way, and only the peer transition is skipped.
    const reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const root = canvasRef.current;
    if (!root) return;
    const row = root.querySelector(`[data-band-nodes="${reorder.band}"]`);
    if (!row) return;

    // ── Re-baseline the carried card ─────────────────────────────────────────
    // ⚠️ THIS IS THE FIX FOR THE CARD FLICKING AWAY FROM THE CURSOR.
    //
    // The lifted card is a grid item like any other, so when the order changes
    // its SLOT moves — layout puts it somewhere new. Its transform, though, is
    // `cursor - pressOrigin`, measured from where the press started. Those two
    // disagree the instant the slot moves: the card renders at
    // `newSlot + oldOffset` and visibly jumps by the distance between the slots,
    // then carries on tracking the cursor from the wrong place. That jump is
    // the flick, and it fired on every crossing.
    //
    // The cure is to move the ORIGIN by however far the slot moved, so the
    // rendered position is unchanged across the reorder:
    //
    //   rendered = layout + (cursor - origin)
    //   layout moves by d  ⇒  origin must move by d to hold `rendered` still
    //
    // `prev` is the rect BEFORE the reorder and already includes the transform,
    // and `next` is measured after it with the same transform still applied —
    // so their difference is exactly the slot's movement.
    const carried = nodeRefs.current[reorder.nodeId];
    const carriedPrev = before.get(reorder.nodeId);
    const press = pressRef.current;
    if (carried && carriedPrev && press) {
      const carriedNext = carried.getBoundingClientRect();
      press.x += carriedNext.left - carriedPrev.left;
      press.y += carriedNext.top - carriedPrev.top;
      // Rewritten in the same layout pass, before paint, so the corrected
      // position is the first one shown rather than a frame of the wrong one.
      if (press.cx != null) {
        carried.style.transform = `translate(${press.cx - press.x}px, ${
          press.cy - press.y
        }px)`;
      }
    }

    if (reduceMotion) return;

    row.querySelectorAll("[data-wig-id]").forEach((card) => {
      const id = card.getAttribute("data-wig-id");
      // The carried card is handled above — it is re-baselined rather than
      // FLIPped, because it must stay pinned to the cursor rather than animate
      // between slots.
      if (id === reorder.nodeId) return;

      const prev = before.get(id);
      if (!prev) return;
      const next = card.getBoundingClientRect();
      const dx = prev.left - next.left;
      const dy = prev.top - next.top;
      // Sub-pixel deltas are cards that did not really move. Animating them
      // costs a transition on every card in the row for no visible change.
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;

      card.style.transition = "none";
      card.style.transform = `translate(${dx}px, ${dy}px)`;
      // Two frames, not one. A single rAF still lands inside the frame that
      // applied the inverting transform, so the browser coalesces the two
      // writes and no transition runs — the card jumps, which is the bug this
      // whole effect exists to fix.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          card.style.transition = "transform 180ms cubic-bezier(0.2,0,0,1)";
          card.style.transform = "";
        });
      });
    });
  }, [reorder]);

  // ── The reorder gesture ───────────────────────────────────────────────────
  // Always mounted, unlike the connector-drag effect above, because it has to
  // hear the very first mousemove after a press — and at that moment there is no
  // `reorder` state yet to key an effect on. The handlers are cheap no-ops while
  // `pressRef` is empty, which is almost always.
  //
  // Window-level rather than on the card for the ordinary reason: the pointer
  // leaves the card as soon as it moves, and a mouseup outside it must still end
  // the drag rather than leaving the canvas holding a card forever.
  useEffect(() => {
    if (readOnly) return undefined;

    const handleMove = (event) => {
      const press = pressRef.current;
      if (!press) return;

      // A press that may never become a drag — the BHAG. It stays armed so the
      // click still selects on mouseup; it simply never lifts.
      if (!press.canDrag) return;

      if (!press.dragging) {
        const far =
          Math.abs(event.clientX - press.x) > REORDER_THRESHOLD ||
          Math.abs(event.clientY - press.y) > REORDER_THRESHOLD;
        if (!far) return;
        press.dragging = true;
        // Picking a card up clears the focus ring. Focus fades every unrelated
        // card, and dragging a card through a row of faded ones makes the
        // landing zone unreadable — the very thing being aimed at.
        setFocusedNodeId(null);
        setSelectedConnectionId(null);
      }

      // ── 1. The carried card follows the cursor, WITHOUT a re-render ───────
      // ⚠️ WRITTEN STRAIGHT TO THE DOM, AND THAT IS THE WHOLE REASON THIS DRAG
      // IS SMOOTH (2026-09-14). This used to live in `reorder` state, so every
      // mousemove re-rendered a canvas of ~20 cards plus two SVG overlays of
      // paths, and the layout effect re-measured every card's rect on top of
      // it. At 60+ moves a second the card visibly trailed the pointer.
      //
      // A transform on one element is a compositor-only change: no React, no
      // layout, no measure. The card is pinned under the cursor at the point it
      // was grabbed rather than snapping its centre to the pointer.
      // The live cursor, kept on the press so the re-baselining below can
      // recompute this exact transform after the card's slot has moved.
      press.cx = event.clientX;
      press.cy = event.clientY;

      const lifted = nodeRefs.current[press.nodeId];
      if (lifted) {
        lifted.style.transform = `translate(${event.clientX - press.x}px, ${
          event.clientY - press.y
        }px)`;
      }

      // ── 2. React only hears about it when the SLOT changes ────────────────
      const index = dropIndexAt(
        press.bandKey,
        press.nodeId,
        event.clientX,
        event.clientY,
      );
      // Pointer left the row entirely. Keep the last valid slot rather than
      // falling home, so wandering out and back doesn't make the gap flicker.
      if (index === null || index === press.index) return;

      // ⚠️ THE PENDING INDEX LIVES ON THE REF, AND THE STATE IS ONLY FOR
      // DRAWING. `handleUp` needs to know where the card landed, and reading it
      // out of a `setReorder` updater would mean calling `onReorderNodes` from
      // inside one — which StrictMode is entitled to invoke twice, firing the
      // reorder (and later the socket emit behind it) two times for one drop.
      // A ref is read once, by the handler that owns the gesture.
      press.index = index;

      // Snapshot every card's position BEFORE the reorder, for the FLIP in
      // `useLayoutEffect` below. Taken here rather than there because here is
      // the last moment the old layout still exists.
      flipRef.current = captureRects(press.bandKey);

      setReorder({
        nodeId: press.nodeId,
        band: press.bandKey,
        index,
      });
    };

    const handleUp = () => {
      const press = pressRef.current;
      pressRef.current = null;
      if (!press) return;

      // The inline transform was written outside React, so React will not
      // clean it up — the card would stay parked wherever it was dropped.
      const lifted = nodeRefs.current[press.nodeId];
      if (lifted) lifted.style.transform = "";

      // A press that never passed the threshold is a click, and the card's own
      // mouseup selects it — see `WigNode`. Nothing to commit.
      if (!press.dragging) {
        setReorder(null);
        return;
      }

      // One measure for the whole gesture, here rather than on every move —
      // the connectors have been faded and stale throughout, and this is the
      // pass that makes them true again.
      setReorder(null);
      measure();

      if (press.index == null || !onReorderNodes) return;

      const ids = nodes
        .filter((node) => node.band === press.bandKey)
        .map((node) => node.id);
      const next = moveWithin(ids, press.nodeId, press.index);

      // Only when something actually moved. A drag that ends where it started
      // is a no-op, and reporting it would push an identical order to the
      // server and, later, to the other person in the session — a visible
      // flicker on their screen in exchange for nothing.
      if (next.some((id, i) => id !== ids[i])) {
        onReorderNodes(press.bandKey, next);
      }
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [readOnly, nodes, onReorderNodes, dropIndexAt, captureRects, measure]);

  useEffect(() => {
    if (!flash) return undefined;
    const timer = setTimeout(() => setFlash(""), 2600);
    return () => clearTimeout(timer);
  }, [flash]);

  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === "Escape") {
        setDrag(null);
        setFocusedNodeId(null);
        setSelectedConnectionId(null);
        // Abandons a reorder as well, and abandons it properly: clearing
        // `pressRef` is what stops the still-pending mouseup from committing
        // the move the user just pressed Escape to call off. Dropping only the
        // `reorder` state would snap the card home and then move it anyway.
        pressRef.current = null;
        setReorder(null);
        return;
      }
      if (event.key !== "Delete" && event.key !== "Backspace") return;
      if (!selectedConnectionId) return;
      const tag = (event.target.tagName || "").toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        event.target.isContentEditable
      ) {
        return;
      }
      event.preventDefault();
      if (readOnly) return;
      onDeleteConnection(selectedConnectionId);
      setSelectedConnectionId(null);
      setFlash("Connection deleted");
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [selectedConnectionId, onDeleteConnection, readOnly]);

  // ── Picking a card up ─────────────────────────────────────────────────────
  // Only arms the gesture; the drag itself starts in `handleMove` once the
  // pointer has passed `REORDER_THRESHOLD`. That ordering is what lets one
  // mousedown serve both "select this card" and "move this card" — the press is
  // ambiguous until the pointer proves otherwise.
  const beginPress = (nodeId, bandKey, event) => {
    // A connector drag owns the pointer; never both at once.
    //
    // ⚠️ `readOnly` IS NOT A REASON TO SKIP THIS, and treating it as one broke
    // the locked map. Selecting a card is a READ action — it highlights what
    // that WIG connects to and fades the rest — and it is the only thing the
    // locked map is for. Returning here left `endPress` with no press to match
    // on mouseup, so clicking a card on a locked blueprint did nothing at all,
    // while the hint line underneath still said "Select any WIG to explore its
    // connections."
    //
    // The press is armed in every mode; what `readOnly` removes is the DRAG,
    // via `canDrag` below and the reorder listeners, which are not even bound
    // when read-only.
    if (drag) return;
    // Anything but the primary button: let the browser do its thing.
    if (event.button !== 0) return;
    pressRef.current = {
      nodeId,
      bandKey,
      x: event.clientX,
      y: event.clientY,
      // Whether it has become a drag yet. Read on mouseup to decide if this was
      // a click (select) or the end of a drag (commit).
      dragging: false,
      // ⚠️ BHAG IS NOT DRAGGABLE, BUT IT IS STILL CLICKABLE — and conflating
      // those two cost the BHAG its focus behaviour entirely. This used to
      // `return` before arming the press at all, so `endPress` found nothing on
      // mouseup and clicking the BHAG stopped highlighting the WIGs connected
      // to it. The press is always armed now; only the DRAG is refused, in
      // `handleMove`.
      //
      // Why it cannot be reordered: `reorder_section_items` is keyed by
      // `section_key`, and the BHAG band is a presentational row built from the
      // blueprint's single BHAG string. There is no section behind it to name —
      // the server would answer `section_not_found` — and it is a row of one, so
      // there is nothing to reorder even locally.
      canDrag: !readOnly && bandKey !== "bhag",
    };
  };

  // The click half of the press. Runs on the card's own mouseup, which React
  // delivers before the window listener that commits a drag — so `pressRef` is
  // still intact here and can answer "was this a click, and was it on THIS
  // card?".
  const endPress = (nodeId) => {
    const press = pressRef.current;
    if (!press || press.dragging || press.nodeId !== nodeId) return;
    // ⚠️ CLEARED HERE, not only in the drag's `handleUp`. Those window listeners
    // are not bound when read-only, so on a locked map nothing else would ever
    // release the press — and a stale one is a real hazard rather than untidy:
    // the next press overwrites it, but a mouseup that arrives without one
    // (Escape mid-press, a card unmounting under the pointer) would be matched
    // against a press the user has moved on from.
    pressRef.current = null;
    setSelectedConnectionId(null);
    setFocusedNodeId((prev) => (prev === nodeId ? null : nodeId));
  };

  const handlePointDown = (nodeId, event) => {
    // A connection point sits ON the card, so its mousedown would otherwise arm
    // the reorder press underneath it and the first pixel of a connector drag
    // would lift the card instead of drawing a line.
    pressRef.current = null;
    setSelectedConnectionId(null);
    setDrag({ mode: "create", fromId: nodeId, cursor: toCanvasPoint(event) });
  };

  const handleEndpointDown = (connection, end, event) => {
    event.stopPropagation();
    event.preventDefault();
    const anchorId = end === "from" ? connection.to : connection.from;
    setDrag({
      mode: "reconnect",
      connectionId: connection.id,
      end,
      fromId: anchorId,
      cursor: toCanvasPoint(event),
    });
  };

  const clearAll = () => {
    setFocusedNodeId(null);
    setSelectedConnectionId(null);
  };

  const connectMode = !!drag;
  const reordering = !!reorder;

  // ── The hint line ─────────────────────────────────────────────────────────
  // Shown on BOTH screens (2026-09-03, on instruction: the read-only view keeps
  // the status line and loses only the legend beside it).
  //
  // ⚠️ THE READ-ONLY BRANCH MUST COME FIRST. Every edit branch below describes
  // a gesture — "drag from a point to add one", "press Delete to remove" — and
  // telling someone to drag a point that isn't rendered is worse than saying
  // nothing: they try, nothing happens, and the screen looks broken rather than
  // finished. That exact string on the locked map was reported as wrong once
  // already, so the guard is an early return rather than a condition threaded
  // through the branches, where a later edit could miss one.
  //
  // Two strings, not five. `flash` and the `drag` states cannot arise without
  // the affordances that produce them, and `selectedConnectionId` cannot either
  // — line selection is disabled when read-only. They are unreachable here by
  // construction, and this branch means they stay unreachable by intent.
  const hint = (() => {
    if (readOnly) {
      if (focusedNodeId)
        return { text: "Showing direct connections", tone: "armed" };
      return {
        text: "Select any WIG to explore its connections.",
        tone: "info",
      };
    }
    // Before `flash`: a drag in progress is the most current thing on screen,
    // and a stale success message sitting over a live gesture is noise.
    if (reordering)
      return { text: "Drop to reorder within this row", tone: "armed" };
    if (flash) return { text: flash, tone: "success" };
    // ⚠️ Before the plain hover branch, so a target that cannot accept the drop
    // says why instead of inviting a release that will be refused. The reason
    // string comes from `rejectionFor`, the same call that colours the line red
    // and that `handleUp` will act on.
    if (drag && hoverTargetId && hoverRejection)
      return { text: hoverRejection, tone: "danger" };
    if (drag && hoverTargetId)
      return { text: "Release to create the connection", tone: "armed" };
    if (drag) return { text: "Drag to another WIG to connect", tone: "armed" };
    if (selectedConnectionId)
      return {
        text: "Drag an endpoint to reconnect, or press Delete to remove",
        tone: "info",
      };
    if (focusedNodeId)
      return {
        text: "Showing direct connections — drag from a point to add one",
        tone: "armed",
      };
    // The idle line carries the reorder gesture too, because nothing else does:
    // a card that can be dragged looks exactly like one that can't, and the
    // grab cursor only appears once the pointer is already on it.
    return {
      text: "Select any WIG to explore its connections, or drag one to reorder its row.",
      tone: "info",
    };
  })();

  // ── What each band renders, mid-drag included ─────────────────────────────
  // While a reorder is in flight this applies the pending move to the DRAWN
  // order only. Nothing is told to anyone until the drop — `onReorderNodes`
  // fires once, from `handleUp` — so an abandoned drag leaves no trace.
  //
  // Reordering the real array is what opens the gap: the cards are grid items,
  // so moving one in the list makes its neighbours slide into the space it left
  // and open a space where it's going. That is the whole "cards shift to show
  // the drop" behaviour, and it costs a `map` rather than a second rendering
  // path with placeholder elements in it.
  const nodesByBand = bands.map((band) => {
    const items = nodes.filter((node) => node.band === band.key);
    if (!reorder || reorder.band !== band.key || reorder.index === null) {
      return { band, items };
    }
    const ids = moveWithin(
      items.map((node) => node.id),
      reorder.nodeId,
      reorder.index,
    );
    const byId = new Map(items.map((node) => [node.id, node]));
    return { band, items: ids.map((id) => byId.get(id)).filter(Boolean) };
  });

  // ── Why the connectors are drawn in two overlays ──────────────────────────
  // Lines normally sit BEHIND the cards, so a line passing a card it isn't
  // connected to disappears under it instead of looking like it terminates
  // there. But that's wrong for the lines being highlighted: the whole point
  // of clicking a card is to trace its connections, and the one thing that
  // must not happen is the line you're trying to follow vanishing under an
  // unrelated card halfway along.
  //
  // So highlighted lines move to a second overlay stacked ABOVE the cards.
  // Only the focused set moves; everything faded stays behind, which is also
  // what keeps the highlighted paths from being lost in the traffic. The
  // endpoint cards themselves are raised above this overlay in the stylesheet,
  // so a line still tucks under the two cards it actually joins.
  const renderConnection = (connection) => {
    const beingReconnected =
      drag && drag.mode === "reconnect" && drag.connectionId === connection.id;
    if (beingReconnected) return null;

    const geo = geometry[connection.id];
    if (!geo) return null;
    const { start, end, d, detour } = geo;
    const isSelected = selectedConnectionId === connection.id;
    const inFocus = focusSet
      ? focusSet.connectionIds.has(connection.id)
      : false;
    const isFaded = !!focusSet && !inFocus;
    const isAi = connection.source === CONNECTION_SOURCE.AI;

    // The head has to match its own stroke's colour, or an AI-suggested line
    // ends in a head darker than the line it belongs to - which is itself a
    // cause of arrowheads looking detached from their lines.
    const marker = isSelected
      ? "wcx-arrow-select"
      : isFaded
        ? "wcx-arrow-faded"
        : inFocus
          ? "wcx-arrow-active"
          : isAi
            ? "wcx-arrow-ai"
            : "wcx-arrow";

    return (
      <g
        key={connection.id}
        className={[
          "wcx-line",
          isSelected ? "is-selected" : "",
          inFocus ? "is-focus" : "",
          isFaded ? "is-faded" : "",
          isAi ? "is-ai" : "",
          detour ? "is-detour" : "",
          connectMode ? "is-muted" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {/* Invisible fat stroke underneath - a 1.4px path is far too thin a
            hit target for "click a line to select it". */}
        {/* ⚠️ Not selectable when read-only. Selection exists to expose the
            reconnect handles and the Delete key, neither of which applies to a
            locked blueprint — and a line that highlights and then offers
            nothing is a dead end. Focusing a NODE still works, which is the
            read affordance this screen is for. */}
        <path
          className="wcx-line-hit"
          d={d}
          onMouseDown={
            readOnly
              ? undefined
              : (event) => {
                  event.stopPropagation();
                  setFocusedNodeId(null);
                  setSelectedConnectionId(connection.id);
                }
          }
        />
        <path
          className="wcx-line-stroke"
          d={d}
          // ⚠️ NORMALISES THE LINE'S LENGTH TO 1 FOR DASH PURPOSES, which is
          // what lets the focus draw-on animation work at any path length.
          //
          // That animation is `stroke-dasharray` + `stroke-dashoffset`, and
          // those are measured in PATH LENGTH. They were hard-coded to 1200px
          // on the theory that 1200 exceeded the longest path here. Ten columns
          // ended that: a WIG at one end of a band connecting to a card at the
          // other end of the band above traces ~2400px, so the dash covered the
          // first 1200 and the rest of the stroke was never painted at all. The
          // arrowhead is a marker and sits at the path's true end regardless —
          // hence "the line breaks in the middle and only the arrow shows".
          //
          // `pathLength="1"` makes the browser scale every dash measurement to
          // the path's real length, so a dasharray of 1 is exactly one path, one
          // time, whatever the geometry. No constant to outgrow. See
          // `.wcx-line.is-focus .wcx-line-stroke` in WigConnections.scss.
          pathLength="1"
          markerEnd={`url(#${marker})`}
        />

        {/* `!readOnly` is belt AND braces: with selection disabled above,
            `isSelected` can't be true on a locked canvas anyway. Stated here so
            the invariant is local to the thing it protects rather than depending
            on a guard 200 lines away. */}
        {isSelected && !readOnly && (
          <>
            <circle
              className="wcx-endpoint"
              cx={start.x}
              cy={start.y}
              r={5}
              onMouseDown={(event) =>
                handleEndpointDown(connection, "from", event)
              }
            />
            <circle
              className="wcx-endpoint"
              cx={end.x}
              cy={end.y}
              r={5}
              onMouseDown={(event) =>
                handleEndpointDown(connection, "to", event)
              }
            />
          </>
        )}
      </g>
    );
  };

  // A selected line is raised too, not just a focused one: its endpoint
  // handles have to be grabbable, and a handle sitting under a card isn't.
  const isRaised = (connection) =>
    (focusSet && focusSet.connectionIds.has(connection.id)) ||
    selectedConnectionId === connection.id;
  const groundConnections = connections.filter((c) => !isRaised(c));
  const raisedConnections = connections.filter(isRaised);

  return (
    <div className="wcx-viewport">
      <div className="wcx-scroll" ref={scrollRef}>
        {/* Reserves the SCALED footprint so the scroll range matches what's on
            screen. A transform doesn't change layout size, so without this
            wrapper the scrollbars describe the unscaled canvas and zooming in
            leaves most of the map unreachable - which is what made zoom feel
            broken before. */}
        <div
          className="wcx-zoom"
          style={{
            width: size.w ? size.w * scale : undefined,
            height: size.h ? size.h * scale : undefined,
          }}
        >
          {/* Click-away-to-deselect on the canvas ground. Deliberately NOT
              given a role: this is the absence of a target, not a control, and
              announcing the whole canvas as a button would be actively
              misleading to a screen reader. Escape already clears the selection
              from the keyboard, which is the accessible path. */}
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div
            // ⚠️ `is-reordering` HIDES EVERY CONNECTOR (2026-09-14, on
            // instruction: "when drag and drop we will not show those
            // connection"). It was a fade; hiding outright is the better call
            // and for a reason beyond taste — the paths on screen during a drag
            // were computed from rects the cards have already left, so a faded
            // line is still a WRONG line, just a quieter one. With the cards
            // sliding into new slots underneath them, half-transparent wrong
            // lines read as the map breaking.
            //
            // Hidden, the drag is just cards and space, which is the only thing
            // being decided. They come back on drop, against geometry measured
            // once the order has settled, so what reappears is correct.
            className={`wcx-canvas ${focusedNodeId ? "is-focus-mode" : ""} ${
              reordering ? "is-reordering" : ""
            }`}
            ref={canvasRef}
            style={{ transform: `scale(${scale})` }}
            onMouseDown={clearAll}
          >
            {nodesByBand.map(({ band, items }) => (
              <div className={`wcx-band wcx-band--${band.tone}`} key={band.key}>
                <div className="wcx-band-label">
                  <span>{band.label}</span>
                </div>
                <div
                  className={`wcx-band-nodes ${
                    band.key === "bhag" ? "is-bhag" : ""
                  }`}
                  // Read by `dropIndexAt` to find this band's cards without
                  // depending on the DOM shape around them.
                  data-band-nodes={band.key}
                >
                  {items.map((node) => (
                    <WigNode
                      key={node.id}
                      node={node}
                      bandKey={band.key}
                      // The card being carried. It follows the cursor and leaves
                      // its slot behind as the gap.
                      lifted={!!reorder && reorder.nodeId === node.id}
                      // Every other card in the SAME band, while a drag is on.
                      // They're the ones that will move, and saying so is what
                      // makes "only within this row" legible without a rule
                      // written anywhere on screen.
                      reorderPeer={
                        !!reorder &&
                        reorder.band === band.key &&
                        reorder.nodeId !== node.id
                      }
                      onBeginPress={beginPress}
                      onEndPress={endPress}
                      tooltipsSuppressed={connectMode || reordering}
                      // Not offered on the BHAG band — it is not a section and
                      // has no item to delete — nor when the band is down to
                      // its last WIG, which the server would refuse as
                      // `item_floor_reached`.
                      onRemove={
                        onRemoveItem &&
                        band.key !== "bhag" &&
                        canDeleteLiveReviewItem(items.length)
                          ? onRemoveItem
                          : null
                      }
                      focused={focusedNodeId === node.id}
                      neighbour={
                        !!focusSet &&
                        focusedNodeId !== node.id &&
                        focusSet.nodeIds.has(node.id)
                      }
                      faded={!!focusSet && !focusSet.nodeIds.has(node.id)}
                      isTarget={hoverTargetId === node.id}
                      targetInvalid={
                        hoverTargetId === node.id && !!hoverRejection
                      }
                      dimmed={
                        connectMode &&
                        node.id !== drag.fromId &&
                        node.id !== hoverTargetId
                      }
                      connectMode={connectMode}
                      readOnly={readOnly}
                      onSelect={(id) => {
                        setSelectedConnectionId(null);
                        setFocusedNodeId((prev) => (prev === id ? null : id));
                      }}
                      onPointDown={handlePointDown}
                      registerRef={registerRef}
                    />
                  ))}

                  {/* ── The add tile ──
                      A CARD, in the next grid slot: three WIGs in a row means
                      this is the fourth cell, same size and same position as a
                      real card would occupy. That is the whole idea — the empty
                      slot is what the new WIG will fill, so the affordance is
                      literally the shape of the thing it makes.

                      Clicking opens a dialog rather than expanding in place.
                      The cell is a fixed 200px column and the canvas sits under
                      a pan/zoom transform: a form growing inside a band would
                      be three slivers wide AND would reflow the geometry the
                      connector lines are measured against, mid-typing.

                      `bhag` is excluded — not a section, nothing to add to. At
                      the cap the tile goes, since there is no empty slot left
                      to represent; the dialog is where counts get explained. */}
                  {onAddRequest &&
                  !readOnly &&
                  band.key !== "bhag" &&
                  items.length < LIVE_REVIEW_SECTION_MAX_ITEMS ? (
                    <button
                      type="button"
                      className="wcx-node wcx-node--add"
                      onClick={() => onAddRequest(band.key)}
                    >
                      <span className="wcx-node-add-plus" aria-hidden="true">
                        +
                      </span>
                      <span className="wcx-node-add-text">Add WIG</span>
                    </button>
                  ) : null}
                </div>
              </div>
            ))}

            <svg
              className="wcx-lines"
              width={size.w}
              height={size.h}
              viewBox={`0 0 ${size.w || 1} ${size.h || 1}`}
            >
              {/* Arrowheads. Four separate traps here, each of which on its
                  own makes a head look wrong, and all four were hit at some
                  point in this build:

                  1. `markerUnits` defaults to `strokeWidth`, so a 7-unit
                     marker rendered at 7 x 1.4 = ~10px on a normal line and
                     7 x 2.2 = ~15px on a focused one - oversized AND
                     inconsistent between states. `userSpaceOnUse` pins them
                     to real pixels.
                  2. A head as wide as it is tall reads as a triangle sitting
                     on the line rather than as the line's own tip. Keep it
                     longer than it is wide: ARROW_LEN x ARROW_W (13 x 9).
                  3. `auto-start-reverse` only means anything for
                     `marker-start`; plain `auto` is correct for a marker-end
                     and rotates the head to the path's tangent at the
                     endpoint. But note it can only be as angled as that
                     tangent - if heads look uniformly straight, the curve's
                     last control point is the suspect, not the marker (see
                     ARRIVAL_LEAN).
                  4. `refX` near the tip leaves the stroke ending where the
                     head is zero pixels wide, so it spikes out through the
                     point. Anchor it at the back of the head instead and trim
                     the path to meet it - see trimmedCurve.
                  5. But "the back of the head" is NOT x=0 on a concave head:
                     the solid body starts at the notch vertex, so anchoring at
                     x=0 leaves a visible gap between the line and its arrow.
                     `refX` is ARROW_NOTCH and the trim is ARROW_TRIM.
                  6. The size is not free. ARROW_TRIM must equal the head's
                     reach forward of its anchor, and ARROW_W sets the floor for
                     FAN_STEP, since two heads arriving on one card edge need
                     more than a head's width between them. Resizing the arrow
                     means revisiting both. */}
              <defs>
                {[
                  ["wcx-arrow", "#0f766e"],
                  ["wcx-arrow-ai", "#8fb8b4"],
                  ["wcx-arrow-active", "#0b5f58"],
                  ["wcx-arrow-select", "#2563eb"],
                  ["wcx-arrow-faded", "#dde7e7"],
                ].map(([id, fill]) => (
                  <marker
                    key={id}
                    id={id}
                    viewBox={`0 0 ${ARROW_LEN} ${ARROW_W}`}
                    refX={ARROW_NOTCH}
                    refY={ARROW_W / 2}
                    markerWidth={ARROW_LEN}
                    markerHeight={ARROW_W}
                    markerUnits="userSpaceOnUse"
                    orient="auto"
                  >
                    {/* Concave back rather than a flat-based triangle. The
                        notch (the final `L ARROW_NOTCH` before closing) makes
                        the head read as swept along its line instead of as a
                        wedge stuck on the end, and it pulls the silhouette in
                        at the base - which is where two neighbouring heads in a
                        fan would otherwise come closest to touching.

                        Shallower than it used to be, proportionally: at the
                        old 9px length the notch was 23% of the head, which at
                        this size would read as a chevron rather than an arrow.
                        20% keeps the swept feel with a solid arrow silhouette.

                        The notch vertex is the anchor point (refX above), so it
                        is exactly where the stroke ends - trap 5. */}
                    <path
                      d={`M 0 0 L ${ARROW_LEN} ${ARROW_W / 2} L 0 ${ARROW_W} L ${ARROW_NOTCH} ${
                        ARROW_W / 2
                      } Z`}
                      fill={fill}
                    />
                  </marker>
                ))}
              </defs>

              {groundConnections.map(renderConnection)}

              {/* Preview only - dashed, no arrowhead. It isn't a connection
                  yet and shouldn't look like one. Curved on the same rule as
                  the real ones so the drag previews its own result. */}
              {drag &&
                rects[drag.fromId] &&
                (() => {
                  const rect = rects[drag.fromId];
                  const cx = rect.x + rect.w / 2;
                  const cy = rect.y + rect.h / 2;
                  const dx = drag.cursor.x - cx;
                  const dy = drag.cursor.y - cy;
                  // No target card yet, so there's no grid position to key
                  // off - the cursor's own direction picks the side, and the
                  // same axis then bends the curve.
                  const vertical = Math.abs(dy) >= Math.abs(dx);
                  const side = vertical
                    ? dy >= 0
                      ? SIDE.BOTTOM
                      : SIDE.TOP
                    : dx >= 0
                      ? SIDE.RIGHT
                      : SIDE.LEFT;
                  const start = anchorAt(rect, side, 0);
                  // No trim: the preview has no arrowhead, so it should run
                  // all the way to the cursor.
                  const { d } = trimmedCurve(
                    start,
                    drag.cursor,
                    vertical ? AXIS.V : AXIS.H,
                    0,
                  );
                  // Red when the card under the cursor would refuse the drop —
                  // the connection already exists, or it would be a WIG
                  // connecting to itself. `is-invalid` also drops the dashes:
                  // a dashed line reads as "forming", and this one is not
                  // going to form.
                  return (
                    <path
                      className={`wcx-preview${
                        hoverRejection ? " is-invalid" : ""
                      }`}
                      d={d}
                    />
                  );
                })()}
            </svg>

            {/* Second overlay, above the cards - see renderConnection's note.
                Only mounted when something is actually raised, so the ordinary
                view has exactly one overlay and no extra stacking context.
                The markers live in the first SVG's <defs>; url(#id) resolves
                document-wide, so they're shared rather than duplicated. */}
            {raisedConnections.length > 0 && (
              <svg
                className="wcx-lines wcx-lines--raised"
                width={size.w}
                height={size.h}
                viewBox={`0 0 ${size.w || 1} ${size.h || 1}`}
              >
                {raisedConnections.map(renderConnection)}
              </svg>
            )}
          </div>
        </div>
      </div>

      {/* ── The footer ───────────────────────────────────────────────────────
          ⚠️ NO LEGEND, ANYWHERE (2026-09-03, on instruction). The Confirmed /
          AI Suggested / Preview swatch row is gone from BOTH screens. It had
          been removed, restored, and is now removed for good — recorded so the
          next person does not restore it a second time.

          It is replaced by one line of copy on the LIVE screen only: the thing
          the legend was really there to communicate is that the map arrives
          pre-drawn by the AI and the coach is allowed to change it. A swatch
          row made that a colour-matching exercise; a sentence just says it.

          The line is `!readOnly` because both halves of it are false on a
          locked map — nothing can be changed, and by then the connections have
          been confirmed, so calling them "AI-suggested" would be wrong as well
          as useless.

          The status line stays on both screens (see the `readOnly` branch on
          `hint`). On read-only it is the footer's only child, which is why the
          stylesheet right-aligns a lone hint explicitly — `space-between`
          would otherwise put it on the left. */}
      <div className="wcx-footer">
        {!readOnly && (
          <p className="wcx-note mb-0">
            Your AI coach suggested these connections – change any of them, or add your own.
          </p>
        )}
        <div
          className={`wcx-hint wcx-hint--${hint.tone}`}
          role="status"
          aria-live="polite"
        >
          {hint.tone === "success" ? "✓" : "ⓘ"} {hint.text}
        </div>
      </div>
    </div>
  );
};

export default WigConnectionsCanvas;
